import React, { useState, useEffect, useRef } from "react";
import MaterialTable from "material-table";
import { ToastContainer, toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import ContentLoader from "react-content-loader";
import axios from "axios";

import GetCompanyInfoService from "../../services/company/getCompanyInfoService";
import AddPartyRoleSettingService from "../../services/settings/AddPartyRoleSettingService";
import { getTrans as t } from "../../Localization/translations";
import PartyAddRole from "./PartyAddRole";
import EditPartyRole from "./EditPartyRole";
import { API } from "../../backend";
import authHeader from "../../services/auth-header";
import { useNavigate } from "react-router-dom";

function PartyRoleSettings() {
  const tableRef = useRef();
  const navigate = useNavigate();

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");

  useEffect(() => {
    fetchRoles();
  }, []);

  // ✅ CHECK ROLE USAGE
  const checkRoleDeletable = async (roleName) => {
    try {
      const response = await axios.get(
        `${API}company/party-roles/check-deletable/${encodeURIComponent(roleName)}/`,
        {
          headers: authHeader(),
          params: { t: new Date().getTime() }, // 🔥 prevent caching
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error checking role:", error);
      return { can_delete: false };
    }
  };

  // ✅ FETCH ROLES + ATTACH can_delete
  const fetchRoles = async () => {
    try {
      setLoading(true);

      const response = await GetCompanyInfoService.getCompanySettingInfo();
      const rolesString = response.data.party_roles;

      const roleList = rolesString
        ? await Promise.all(
          rolesString.split(",").map(async (role) => {
            const name = role.trim();

            const res = await checkRoleDeletable(name);

            console.log("API:", name, res); // debug

            return {
              name,
              status: "Active",
              can_delete: res && res.can_delete === true,
            };
          })
        )
        : [];

      console.log("Final Roles:", roleList);

      setRoles(roleList);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ SAVE ROLES
  const saveRoles = (newRoles) => {
    const roleNames = newRoles.map((r) => r.name).join(", ");

    AddPartyRoleSettingService.addPartyRoleSetting(roleNames).then((response) => {
      if (response.data.success) {
        toast.success(response.data.response);
        fetchRoles(); // 🔥 reload with fresh can_delete
      } else {
        toast.error(response.data.error);
      }
    });
  };


  const handleDelete = async (roleName) => {
    const result = await checkRoleDeletable(roleName);

    if (!result.can_delete) {
      toast.error(t(result.message || `Cannot delete role "${roleName}"`));
      return;
    }

    confirmAlert({
      title: "",
      message: `Are you sure you want to delete the role "${roleName}"?`,
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            const newRoles = roles.filter((r) => r.name !== roleName);
            saveRoles(newRoles);
          },
        },
        { label: "No" },
      ],
    });
  };

  if (loading) {
    return (
      <ContentLoader
        viewBox="0 0 1000 550"
        backgroundColor="rgba(0,0,0,0.06)"
        foregroundColor="rgba(0,0,0,0.12)"
      />
    );
  }

  if (showAdd) {
    return (
      <PartyAddRole
        currentRoles={roles}
        onSave={(newName) => {
          const newRoles = [...roles, { name: newName, status: "Active" }];
          saveRoles(newRoles);
          setShowAdd(false);
        }}
        onCancel={() => setShowAdd(false)}
      />
    );
  }

  if (showEdit) {
    return (
      <EditPartyRole
        roleName={selectedRole}
        currentRoles={roles}
        onSave={(newName) => {
          const newRoles = roles.map((r) =>
            r.name === selectedRole ? { ...r, name: newName } : r
          );
          saveRoles(newRoles);
          setShowEdit(false);
        }}
        onCancel={() => setShowEdit(false)}
      />
    );
  }

  return (
    <div className="container-fluid">
      <ToastContainer />

      <div className="page-wrapper col-md-12 filter_contract">
        <div className="mb-0">
          <div className="header pb-0">
            <div className="d-lg-flex mb-2">
              <div>
                <h5 className="mb-3">{t("Party Roles")}</h5>
              </div>

              <div className="ms-auto my-auto mt-lg-0 mt-4">
                <div className="btn btn-primary mb-0" onClick={() => setShowAdd(true)}>
                  +&nbsp;{t("New Role")}
                </div>
              </div>
            </div>

            {/* <button
              className="btn btn-secondary"
              onClick={() => navigate("/setting")}
            >
              {t("Back to Settings")}
            </button> */}
          </div>

          <div className="body">
            <MaterialTable
              tableRef={tableRef}
              style={{
                backgroundColor: "var(--bs-table-bg)",
                borderBottomWidth: "1px",
                boxShadow: "inset 0 0 0 9999px var(--bs-table-accent-bg)",
              }}
              columns={[
                {
                  title: t("Role Name"),
                  field: "name",
                  render: (rowData) => (
                    <>
                      <div className="d-xl-none tc d-md-none d-lg-none">
                        {t("Role Name")}
                      </div>
                      <div>{rowData.name}</div>
                    </>
                  ),
                  headerStyle: {
                    fontWeight: "bold",
                    textAlign: "left",
                  },
                  cellStyle: {
                    textAlign: "left",
                    fontSize: 12,
                  },
                },
              ]}
              data={roles}
              actions={[
                {
                  icon: () => (
                    <img
                      src={process.env.PUBLIC_URL + "/static/images/ic-edit.svg"}
                      alt="edit"
                    />
                  ),
                  tooltip: "Edit",
                  onClick: (event, rowData) => {
                    setSelectedRole(rowData.name);
                    setShowEdit(true);
                  },
                },
                (rowData) => ({
                  icon: () =>
                    rowData.can_delete ? (
                      <img
                        src={process.env.PUBLIC_URL + "/static/images/ic-delete.svg"}
                        alt="delete"
                      />
                    ) : (
                      <img
                        src={process.env.PUBLIC_URL + "/static/images/ic-grey-delete.svg"}
                        alt="delete"
                      />
                    ),
                  tooltip: rowData.can_delete
                    ? "Delete"
                    : "Cannot delete",
                  onClick: (event, rowData) => handleDelete(rowData.name),
                  disabled: !rowData.can_delete,
                }),
              ]}
              options={{
                headerStyle: {
                  backgroundColor: "white",
                  color: "#245C7F",
                  fontSize: 12,
                },
                rowStyle: (rowData, index) =>
                  index % 2 ? { backgroundColor: "#fff" } : {},
                sorting: true,
                search: false,
                toolbar: false,
                paging: false,
                actionsColumnIndex: -1,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default PartyRoleSettings;